import { useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { graphql } from 'app/generated/gql'
import type { ResetSetPasswordMutation } from 'app/generated/gql/graphql'
import { gqlMutation } from 'app/lib/graphqlClient'
import type { ClientType } from 'app/generated/graphql'

// NOTE: setPassword is the mutation that actually saves the new password.
// It validates the reset token (code from the email link), sets the password,
// and returns an auth JWT. The confusingly-named resetPassword mutation
// (used in /forgot page via useForgot.ts) only sends the reset email.
const SET_PASSWORD_MUTATION = graphql(`
  mutation ResetSetPassword($email: String!, $password: String!, $token: String!, $clientType: ClientType!) {
    setPassword(email: $email, password: $password, token: $token, clientType: $clientType) {
      accessToken
    }
  }
`)

export type ResetActionData = {
  success?: boolean
  error?: string
}

export type UseResetReturn = {
  email: string
  code: string
  success: boolean
  error: string | undefined
  isSubmitting: boolean
  onSubmit: (formData: FormData) => void
}

export function useReset(): UseResetReturn {
  const [searchParams] = useSearchParams()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const email = decodeURIComponent(searchParams.get('email') || '')
  const code = searchParams.get('code') || ''

  function onSubmit(formData: FormData) {
    setIsSubmitting(true)
    setError(undefined)
    const password = formData.get('password') as string
    const token = (formData.get('code') as string) || code
    const clientType: ClientType = 'PWA' as ClientType
    gqlMutation(SET_PASSWORD_MUTATION, { email, password, token, clientType })
      .then(({ error: gqlErr }) => {
        if (gqlErr) {
          setError(gqlErr.message)
        } else {
          setSuccess(true)
        }
      })
      .catch(() => setError('An unexpected error occurred'))
      .finally(() => setIsSubmitting(false))
  }

  return { email, code, success, error, isSubmitting, onSubmit }
}
